import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { AvatarAsset } from '@fpr/core/models/asset.models';
import { AssetManager, AssetType } from '@fpr/core/services/asset-manager.service';
import { GuestResponse, Guest } from '@fpr/core/models/guest.models';
import { Dictionary } from '@fpr/core/models/utility.models';
import { VasService } from '@fpr/core/services/vas.service';

@Injectable({
    providedIn: 'root'
})
export class GuestService {
    private readonly assetManager = inject(AssetManager);
    private readonly vasService = inject(VasService);


    private cachedGuestResponse: GuestResponse;

    getGuests(activeGuestSwid: string, refresh: boolean = false): Observable<GuestResponse> {

        // return stored party, when appropriate
        if (!refresh && this.cachedGuestResponse) {
            return of(this.cachedGuestResponse);
        }

        // otherwise, make a request for the party
        return this.vasService
            .get<GuestResponse>(`/v1/park-reservation/users/${activeGuestSwid}/guests`)
            .pipe(
                mergeMap(guestResponse => this.addAvatarAssets(guestResponse)),
                tap(guestResponse => {
                    guestResponse.guests.forEach((guest, index) => guest.index = index);
                    this.cachedGuestResponse = guestResponse;
                })
            );

    }

    getGuestsMap(activeGuestSwid: string, refresh: boolean = false): Observable<Dictionary<Guest>> {
        return this.getGuests(activeGuestSwid, refresh)
            .pipe(
                map(guestResponse => {
                    const guestsMap: Dictionary<Guest> = guestResponse.guests
                        .reduce(function (acc, guest) {
                            acc[guest.xid] = guest;

                            return acc;
                        }, {});

                        return guestsMap;
                })
            );
    }

    updateHealthPolicyPreference(
        activeGuestSwid: string,
        guestXids: string[],
        date: string,
        policyVersion: string
    ): Observable<any> {
        return this.vasService
            .put(`/v1/users/${activeGuestSwid}/preferences/health-policy`, {
                guestXids,
                date,
                policyVersion
            });
    }

    getActiveGuestXid(): string | undefined {
        return this?.cachedGuestResponse?.guests?.find(guest => guest.active)?.xid;
    }

    private addAvatarAssets(partyResponse: GuestResponse): Observable<GuestResponse> {

        const avatarIds = partyResponse.guests
            .filter(guest => guest.avatarId)
            .map(guest => guest.avatarId);

        return this.assetManager.getAssets<AvatarAsset>(avatarIds, AssetType.AVATARS)
            .pipe(
                map(assets => {

                    const guests = partyResponse.guests.map(guest => {
                        const avatarId = guest.avatarId;
                        const avatar = assets[avatarId];

                        return !avatarId ? guest : { ...guest, avatar };
                    });

                    return { ...partyResponse, guests };

                })
            );

    }

}
